// Vue 3 compatible modal plugin that provides the same API as vue-js-modal
// This is a compatibility shim for the migration from Vue 2 to Vue 3

import { reactive } from 'vue';
import ModalContainer from './ModalContainer.vue';

// Reactive state to track modal visibility and params
const modalState = reactive({
  modals: {}, // { modalName: { visible: boolean, params: object } }
});

// Event emitter for modal events
class ModalEventEmitter {
  constructor() {
    this.listeners = {};
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event, callback) {
    if (!this.listeners[event]) return;
    if (callback) {
      this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
    } else {
      delete this.listeners[event];
    }
  }

  emit(event, ...args) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => callback(...args));
  }
}

const emitter = new ModalEventEmitter();

const modalPlugin = {
  show(name, params = {}) {
    if (!modalState.modals[name]) {
      modalState.modals[name] = { visible: false, params: {} };
    }
    modalState.modals[name].params = params;
    modalState.modals[name].visible = true;
    emitter.emit('before-open', { name, params });
  },

  hide(name) {
    if (modalState.modals[name]) {
      modalState.modals[name].visible = false;
      emitter.emit('before-close', { name });
    }
  },

  toggle(name, params = {}) {
    if (modalState.modals[name]?.visible) {
      this.hide(name);
    } else {
      this.show(name, params);
    }
  },

  // Get state for a specific modal
  getState(name) {
    return modalState.modals[name] || { visible: false, params: {} };
  },

  // Event handling
  $on: emitter.on.bind(emitter),
  $off: emitter.off.bind(emitter),
  $emit: emitter.emit.bind(emitter),
};

// Export for use in components
export { modalState, modalPlugin };

// Vue plugin
const Plugin = {
  install(app) {
    if (this.installed) return;
    this.installed = true;

    // Register the modal container component
    app.component('Modal', ModalContainer);

    // Add $modal to global properties
    app.config.globalProperties.$modal = modalPlugin;
  },
};

export default Plugin;
